import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import HTMLDisplay from "../../../utils/HTMLDisplay";
import truncate from "truncate-html";
import axios from "axios";
import "../../../assets/css/DetailsSection.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const hasImage = (value) =>
  Boolean(
    value && value !== "undefined" && value !== "null" && String(value).trim(),
  );

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime()))
    return { month: "NEWS", day: "—", year: "" };
  return {
    month: date.toLocaleString("en", { month: "short" }).toUpperCase(),
    day: String(date.getDate()).padStart(2, "0"),
    year: date.getFullYear(),
  };
};

function DetailsSection({
  datacontent = [],
  downloadSection = [],
  sideNewSection = [],
  advertSection = [],
  initialPagination,
  initialPaginationDownload,
  datacontentDataSearch,
}) {
  const [newsList, setNewsList] = useState(datacontent || []);
  const [downloadList, setDownloadList] = useState(downloadSection || []);
  const [page, setPage] = useState(1);
  const [pageDownload, setPageDownload] = useState(1);
  const [pagination, setPagination] = useState(
    initialPagination || { current_page: 1, last_page: 1 },
  );
  const [paginationDownload, setPaginationDownload] = useState(
    initialPaginationDownload || { current_page: 1, last_page: 1 },
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const currentRead = Array.isArray(newsList) ? newsList : [];
  const sideNews = Array.isArray(sideNewSection) ? sideNewSection : [];
  const downloads = Array.isArray(downloadList) ? downloadList : [];
  const mainItem = currentRead[0] || {};
  //const alignment = String(mainItem.aligment009 || "center").toLowerCase();
  const alignment = "center";

  useEffect(() => {
    setNewsList(datacontent || []);
    setDownloadList(downloadSection || []);
    setPagination(initialPagination || { current_page: 1, last_page: 1 });
    setPaginationDownload(
      initialPaginationDownload || { current_page: 1, last_page: 1 },
    );
  }, [
    datacontent,
    downloadSection,
    initialPagination,
    initialPaginationDownload,
  ]);

  useEffect(() => {
    if (page === 1 && pageDownload === 1) return;
    let live = true;
    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await axios.get(`${BASE_URL}read-data`, {
          params: {
            id: datacontentDataSearch?.value,
            read_page: page,
            download_page: pageDownload,
          },
        });
        if (!live) return;
        const read = response?.data?.data?.readSection;
        const docs = response?.data?.data?.downloadSection;
        setNewsList(read?.data || []);
        setDownloadList(docs?.data || []);
        setPagination(read?.pagination || { current_page: page, last_page: 1 });
        setPaginationDownload(
          docs?.pagination || { current_page: pageDownload, last_page: 1 },
        );
      } catch (err) {
        if (live) {
          console.error("Unable to load details:", err);
          setError(true);
        }
      } finally {
        if (live) setLoading(false);
      }
    };
    load();
    return () => {
      live = false;
    };
  }, [page, pageDownload, datacontentDataSearch?.value]);

  // Keep only the 20 newest stories, then randomly choose up to 3 for this page view.
  // A refresh remounts the page and produces a new selection from the same latest-20 pool.
  const recentUpdates = useMemo(() => {
    const latestTwenty = [...sideNews]
      .sort((a, b) => new Date(b.daterqw || 0) - new Date(a.daterqw || 0))
      .slice(0, 20);

    // Fisher–Yates shuffle: randomize without changing the original prop array.
    for (let i = latestTwenty.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [latestTwenty[i], latestTwenty[j]] = [latestTwenty[j], latestTwenty[i]];
    }

    return latestTwenty.slice(0, 3);
  }, [sideNews]);
  const randomAdvert = useMemo(
    () =>
      advertSection?.length
        ? advertSection[Math.floor(Math.random() * advertSection.length)]
        : null,
    [advertSection],
  );

  const changePage = (next) => {
    const last = Number(pagination?.last_page || 1);
    if (
      next >= 1 &&
      next <= last &&
      next !== Number(pagination?.current_page || 1)
    )
      setPage(next);
  };
  const changePageDownload = (next) => {
    const last = Number(paginationDownload?.last_page || 1);
    if (
      next >= 1 &&
      next <= last &&
      next !== Number(paginationDownload?.current_page || 1)
    )
      setPageDownload(next);
  };

  const renderDate = (value, compact = false) => {
    const date = formatDate(value);
    return (
      <div className={`fmw-detail-date ${compact ? "compact" : ""}`}>
        <span>{date.month}</span>
        <strong>{date.day}</strong>
        <span>{date.year}</span>
      </div>
    );
  };

  const Pagination = ({ data, onChange, label }) => {
    const current = Number(data?.current_page || 1);
    const last = Math.max(1, Number(data?.last_page || 1));
    return (
      <nav className="fmw-detail-pagination" aria-label={label}>
        <span>
          Page <b>{current}</b> <i /> {last}
        </span>
        <div>
          <button
            type="button"
            onClick={() => onChange(1)}
            disabled={current <= 1 || loading}
            aria-label="First page"
          >
            «
          </button>
          <button
            type="button"
            onClick={() => onChange(current - 1)}
            disabled={current <= 1 || loading}
            aria-label="Previous page"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => onChange(current + 1)}
            disabled={current >= last || loading}
            aria-label="Next page"
          >
            ›
          </button>
          <button
            type="button"
            onClick={() => onChange(last)}
            disabled={current >= last || loading}
            aria-label="Last page"
          >
            »
          </button>
        </div>
      </nav>
    );
  };

  if (!currentRead.length && !loading && !error) return null;

  return (
    <section id="service-details" className="fmw-details-v2">
      <div className="container">
        <div className="fmw-detail-intro">
          <div>
            <span className="fmw-detail-eyebrow">
              NEWS · PUBLICATIONS · RESOURCES
            </span>
            <h2>Latest from the Ministry</h2>
          </div>
          <span className="fmw-detail-intro-mark" aria-hidden="true">
            FMW <b>•</b>
          </span>
        </div>

        {loading && (
          <div className="fmw-detail-feedback" role="status">
            <span className="fmw-detail-loader" /> Loading ministry content…
          </div>
        )}
        {error && (
          <div className="fmw-detail-error" role="alert">
            We couldn’t load this page. Please try again.
          </div>
        )}

        <div
          className={`fmw-detail-columns ${alignment !== "center" ? "no-sidebar" : ""}`}
        >
          <main className="fmw-detail-main">
            {currentRead.map((item, index) => {
              const date = formatDate(item.daterqw);
              return (
                <article
                  className="fmw-detail-feature"
                  key={item.id || item.trackerf || index}
                >
                  {hasImage(item.passport) && (
                    <div className="fmw-detail-feature-image">
                      <img
                        src={`${BACKEND_URL}upload/${item.passport}`}
                        alt={item.title || "Ministry news"}
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="fmw-detail-image-label">
                        MINISTRY NEWS
                      </span>
                      {renderDate(item.daterqw)}
                    </div>
                  )}
                  <div className="fmw-detail-feature-content">
                    <div className="fmw-detail-meta">
                      <div className="fmw-detail-meta-text">
                        <span className="fmw-detail-overline">
                          OFFICIAL UPDATE
                        </span>
                        {item.caption && (
                          <div className="fmw-detail-caption">
                            <HTMLDisplay html={item.caption} type="title" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="fmw-detail-title">
                      <HTMLDisplay html={item.story01} type="title" />
                    </div>
                    <div className="fmw-detail-body">
                      <HTMLDisplay html={item.story01} type="body" />
                    </div>
                  </div>
                </article>
              );
            })}
            <Pagination
              data={pagination}
              onChange={changePage}
              label="Article pagination"
            />
          </main>

          {alignment === "center" && (
            <aside className="fmw-detail-sidebar">
              {recentUpdates.length > 0 && (
                <section className="fmw-detail-widget fmw-detail-updates">
                  <div className="fmw-detail-widget-head">
                    <span>STAY INFORMED</span>
                    <h3>More Updates</h3>
                  </div>
                  {recentUpdates.map((item, index) => (
                    <article
                      className="fmw-detail-update"
                      key={item.id || index}
                    >
                      {hasImage(item.passport) && (
                        <Link
                          to={`/read/${item.id}`}
                          className="fmw-detail-update-image"
                        >
                          <img
                            src={`${BACKEND_URL}upload/${item.passport}`}
                            alt={item.title || "Ministry update"}
                            loading="lazy"
                          />
                        </Link>
                      )}
                      <div className="fmw-detail-update-date">
                        {formatDate(item.daterqw).month}{" "}
                        {formatDate(item.daterqw).day},{" "}
                        {formatDate(item.daterqw).year}
                      </div>
                      <h4>
                        <HTMLDisplay html={item.story01} type="title" />
                      </h4>
                      <p>
                        <HTMLDisplay
                          html={item.story01}
                          textNo={30}
                          type="body"
                        />
                      </p>
                      <Link
                        to={`/read/${item.id}`}
                        className="fmw-detail-link"
                      >
                        Read story <span>→</span>
                      </Link>
                    </article>
                  ))}
                </section>
              )}

              {randomAdvert && hasImage(randomAdvert.passport) && (
                <section className="fmw-detail-advert-widget">
                  <div className="fmw-detail-advert-heading">
                    <span>PUBLIC INFORMATION</span>
                    <h3>Awareness Zone</h3>
                  </div>
                  <img
                    src={`${BACKEND_URL}upload/${randomAdvert.passport}`}
                    alt={randomAdvert.title || "Public information"}
                    loading="lazy"
                  />
                </section>
              )}

              <section className="fmw-detail-widget fmw-detail-documents">
                <div className="fmw-detail-widget-head">
                  <span>DOCUMENT CENTRE</span>
                  <h3>Downloadable Resources</h3>
                  {/* <p>Official documents and published resources in one place.</p> */}
                </div>
                {downloads.length ? (
                  <ul>
                    {downloads.map((file, index) => (
                      <li key={file.id || file.addressLink || index}>
                        <span className="fmw-detail-pdf">
                          <i
                            className="fa-solid fa-file-pdf"
                            aria-hidden="true"
                          />
                        </span>
                        <Link
                          to={`${BACKEND_URL}upload/${file.addressLink}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={file.title}
                        >
                          {truncate(file.title || "Download document", 48, {
                            byWords: true,
                          })}
                        </Link>
                        <i
                          className="fa-solid fa-arrow-down-to-line fmw-detail-download-icon"
                          aria-hidden="true"
                        />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="fmw-detail-empty">
                    No documents available at the moment.
                  </p>
                )}
                <Pagination
                  data={paginationDownload}
                  onChange={changePageDownload}
                  label="Download resources pagination"
                />
              </section>
            </aside>
          )}
        </div>
      </div>
    </section>
  );
}

export default React.memo(DetailsSection);
